/**
 * Focused dirty checks for Vehicle Cost Rates Edit All / Save.
 * Ignores profitability depreciation — that is stamped from Fleet Standing Total.
 *
 * Uses field-wise compares (not JSON.stringify) so key order / omitted vs
 * undefined optional fields do not produce false dirty/clean results.
 */

import type {
  FleetCostAssumptionsConfig,
  FleetCostCapitalInputs,
  FleetCostVehicleType,
  TrailerStandingAssumptions,
  TrailerTypeRow,
} from '../types/fleetCostAssumptions';
import type {
  ProfitabilityAssumptionsConfig,
  ProfitabilityDriverCostRate,
} from '../types/profitabilityTruth';
import { normalizeTrailerStanding } from './fleetCostTrailerStanding';
import { acquisitionMethodOf, rentalPeriodOf } from './fleetCapitalAnnualised';

function numEq(a: number | undefined, b: number | undefined): boolean {
  const left = typeof a === 'number' && Number.isFinite(a) ? a : 0;
  const right = typeof b === 'number' && Number.isFinite(b) ? b : 0;
  return left === right;
}

/** Method + lease/rental inputs (purchase fields are compared by the caller). */
function acquisitionEqual(a: FleetCostCapitalInputs, b: FleetCostCapitalInputs): boolean {
  return (
    acquisitionMethodOf(a) === acquisitionMethodOf(b)
    && numEq(a.leaseMonthly, b.leaseMonthly)
    && numEq(a.leaseTermMonths, b.leaseTermMonths)
    && numEq(a.leaseUpfront, b.leaseUpfront)
    && numEq(a.leaseEndFees, b.leaseEndFees)
    && Boolean(a.leaseMaintenanceIncluded) === Boolean(b.leaseMaintenanceIncluded)
    && numEq(a.rentalRate, b.rentalRate)
    && rentalPeriodOf(a) === rentalPeriodOf(b)
  );
}

function trailerTypeEqual(a: TrailerTypeRow, b: TrailerTypeRow): boolean {
  return (
    a.id === b.id
    && a.name === b.name
    && numEq(a.quantity, b.quantity)
    && numEq(a.standing_gbp_per_trailer_year, b.standing_gbp_per_trailer_year)
    && numEq(a.unitCost, b.unitCost)
    && numEq(a.serviceLife, b.serviceLife)
    && numEq(a.residualValue, b.residualValue)
    && numEq(a.initialTaxesFees, b.initialTaxesFees)
    && acquisitionEqual(a, b)
  );
}

function trailerTypesEqual(a: TrailerTypeRow[] | undefined, b: TrailerTypeRow[] | undefined): boolean {
  const left = a ?? [];
  const right = b ?? [];
  if (left.length !== right.length) return false;
  return left.every((row, i) => trailerTypeEqual(row, right[i]));
}

function trailerStandingEqual(
  a: TrailerStandingAssumptions | undefined,
  b: TrailerStandingAssumptions | undefined,
): boolean {
  const left = normalizeTrailerStanding(a);
  const right = normalizeTrailerStanding(b);
  return (
    numEq(left.standing_gbp_per_trailer_year, right.standing_gbp_per_trailer_year)
    && numEq(left.trailer_quantity, right.trailer_quantity)
    && numEq(left.allocate_to_vehicle_quantity, right.allocate_to_vehicle_quantity)
    && left.allocation === right.allocation
    && left.allocate_to_tonnage_class === right.allocate_to_tonnage_class
    && (left.quantity_source ?? 'assets') === (right.quantity_source ?? 'assets')
    && trailerTypesEqual(left.trailer_types, right.trailer_types)
  );
}

function numberMapEqual(
  a: Record<string, number> | undefined | null,
  b: Record<string, number> | undefined | null,
): boolean {
  const left = a ?? {};
  const right = b ?? {};
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    if (!numEq(left[key], right[key])) return false;
  }
  return true;
}

function driverRateEqual(
  a: ProfitabilityDriverCostRate | undefined,
  b: ProfitabilityDriverCostRate | undefined,
): boolean {
  return (
    numEq(a?.mileage_rate, b?.mileage_rate)
    && numEq(a?.driving_hourly_rate, b?.driving_hourly_rate)
    && numEq(a?.waiting_hourly_rate, b?.waiting_hourly_rate)
  );
}

function driverMapEqual(
  a: Record<string, ProfitabilityDriverCostRate> | undefined | null,
  b: Record<string, ProfitabilityDriverCostRate> | undefined | null,
): boolean {
  const left = a ?? {};
  const right = b ?? {};
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    if (!driverRateEqual(left[key], right[key])) return false;
  }
  return true;
}

function vehicleTypeEqual(a: FleetCostVehicleType, b: FleetCostVehicleType): boolean {
  return (
    a.id === b.id
    && a.name === b.name
    && numEq(a.unitCost, b.unitCost)
    && numEq(a.serviceLife, b.serviceLife)
    && numEq(a.residualValue, b.residualValue)
    && numEq(a.conversionCost, b.conversionCost)
    && numEq(a.initialTaxesFees, b.initialTaxesFees)
    && numEq(a.insurance, b.insurance)
    && numEq(a.roadTax, b.roadTax)
    && numEq(a.oLicense, b.oLicense)
    && numEq(a.telematics, b.telematics)
    && numEq(a.cleaning, b.cleaning)
    && numEq(a.parking, b.parking)
    && numEq(a.permits, b.permits)
    && numEq(a.scheduledMaintenance, b.scheduledMaintenance)
    && numEq(a.vehicleCompliance, b.vehicleCompliance)
    && numEq(a.overhead, b.overhead)
    && acquisitionEqual(a, b)
    && numEq(a.purchaseQty, b.purchaseQty)
    && numEq(a.leaseQty, b.leaseQty)
    && numEq(a.rentalQty, b.rentalQty)
    && numEq(a.unsetQty, b.unsetQty)
  );
}

function vehicleTypesEqual(
  a: FleetCostVehicleType[] | undefined,
  b: FleetCostVehicleType[] | undefined,
): boolean {
  const left = a ?? [];
  const right = b ?? [];
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i++) {
    if (!vehicleTypeEqual(left[i], right[i])) return false;
  }
  return true;
}

export function isFleetCostDraftDirty(
  draft: FleetCostAssumptionsConfig | null,
  saved: FleetCostAssumptionsConfig | null,
): boolean {
  if (!draft || !saved) return false;
  return (
    draft.cost_rate_basis !== saved.cost_rate_basis
    || draft.use_case !== saved.use_case
    || !vehicleTypesEqual(draft.vehicle_types_body_style, saved.vehicle_types_body_style)
    || !vehicleTypesEqual(draft.vehicle_types_tonnage, saved.vehicle_types_tonnage)
    || !vehicleTypesEqual(draft.vehicle_types_emergency, saved.vehicle_types_emergency)
    || !trailerStandingEqual(draft.trailer_standing, saved.trailer_standing)
  );
}

/** Fuel / driver / basis only — Standing £/day is derived, not user-edited here. */
export function isProfitabilityRatesDraftDirty(
  draft: ProfitabilityAssumptionsConfig | null,
  saved: ProfitabilityAssumptionsConfig | null,
): boolean {
  if (!draft || !saved) return false;
  return (
    (draft.cost_rate_basis ?? 'body_style') !== (saved.cost_rate_basis ?? 'body_style')
    || (draft.fuel_source ?? 'mileage_rates') !== (saved.fuel_source ?? 'mileage_rates')
    || (draft.standing_cost_policy ?? 'calendar_day') !== (saved.standing_cost_policy ?? 'calendar_day')
    || !numberMapEqual(draft.fuel_cost, saved.fuel_cost)
    || !numberMapEqual(draft.fuel_cost_tonnage, saved.fuel_cost_tonnage)
    || !numberMapEqual(draft.fuel_mpg_tonnage, saved.fuel_mpg_tonnage)
    || !numberMapEqual(draft.tyre_cost, saved.tyre_cost)
    || !numberMapEqual(draft.tyre_cost_tonnage, saved.tyre_cost_tonnage)
    || !numberMapEqual(draft.variable_repair_cost, saved.variable_repair_cost)
    || !numberMapEqual(draft.variable_repair_cost_tonnage, saved.variable_repair_cost_tonnage)
    || !driverMapEqual(draft.driver_cost, saved.driver_cost)
    || !driverMapEqual(draft.driver_cost_tonnage, saved.driver_cost_tonnage)
  );
}
